import { Link, useNavigate, useParams } from "react-router-dom";
import { HomeOutlined } from '@ant-design/icons';
import { useCompany } from "../../../hooks/useCompany";
import { Button, DatePicker, Form, Input, Select, InputNumber, FormInstance } from "antd";
import "../CreateMaterials/styles.css";
import { useForm, Controller } from "react-hook-form";
import useLoading from "../../../hooks/useLoading";
import { api } from "../../../services/axios";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { cnpjMask } from "../../../utils/masks";
import dayjs from 'dayjs';

interface CreateOtherExpenditureForm {
    Typology: string;
    Associated: string;
    Supplier: string;
    CNPJ: string;
    ServiceProductDescription: string;
    Quantity?: number;
    Justification: string;
    ProjectName: string;
    ProjectMacroStage: string;
    AppropriationType: string;
    AccountingAccount: string;
    ManagementControl: string;
    TrackingNumber: string;
    AccountingDate: string | null;
    ExpenditureValue: number;
    Observations?: string;
}

interface OptionsProps {
    value: string;
    label: string;
}

export default function CreateOtherExpenditure() {
    const { company } = useCompany();
    const { toggleLoading } = useLoading();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>(); 
    const isEditMode = !!id; 
    const { Option } = Select;
    const { TextArea } = Input;

    const [typologies, setTypologies] = useState<OptionsProps[]>([]);
    const [projectsNames, setProjectsNames] = useState<OptionsProps[]>([]);
    const [macroStages, setMacroStages] = useState<OptionsProps[]>([]);
    const [appropriationTypes, setAppropriationTypes] = useState<OptionsProps[]>([]);

  
    const [typologySelected, setTypologySelected] = useState<string>();
    const [projectNameSelected, setProjectNameSelected] = useState<string>();
    const [projectIdSelected, setProjectIdSelected] = useState<string>();
    const [macroStageSelected, setMacroStageSelected] = useState<string>();
    const [appropriationTypeSelected, setAppropriationTypeSelected] = useState<string>();

    const { setValue, handleSubmit, getValues, control } = useForm<CreateOtherExpenditureForm>();

    async function handleSaveExpenditure(data: CreateOtherExpenditureForm) {
        toggleLoading(true);
        let payload: { [key: string]: any } = {
            ...data,
            serviceId: company.serviceId,
            companyId: company.companyId
        };

        if (isEditMode) {
            payload = { ...payload, id: id };
        }

        try {
            const request = isEditMode
                ? api.put('OtherExpenditure/Update', payload)
                : api.post('OtherExpenditure/Create', payload);

            const response = await request;

            if (response.data.statusCode === 200) {
                toast.success(`Dispêndio ${isEditMode ? 'atualizado' : 'cadastrado'} com sucesso!`);
                navigate(`/${company.serviceId}/expenditure`);
            } else {
                toast.error(`Erro ao ${isEditMode ? 'atualizar' : 'cadastrar'}${response.data.message != undefined ? response.data.message : "."}`);
            }

        } catch (error: any) {
            toast.error("Ocorreu um erro ao enviar o formulário. Verifique o endpoint e os dados.");
        } finally {
            toggleLoading(false);
        }
    }

    async function getSelectOptions() {
        toggleLoading(true);
        try {
            await api.get(`OtherExpenditure/GetSelectOptions/${company.serviceId}/${company.companyId}`).then((resp) => {
                const response = resp.data;
                if (response.statusCode === 200) {
                    setTypologies(response.data.typologies || []);
                    setProjectsNames(response.data.projectsNames || []);
                    setAppropriationTypes(response.data.appropriationTypes || []);
                }
            });
        } catch (error) {
            toast.error("Não foi possível carregar as opções do formulário.");
            const otherExpenditureTypes = [
                { value: 'obras-civis', label: 'Obras Civis' },
                { value: 'equipamentos', label: 'Equipamentos' },
                { value: 'outros-correlatos', label: 'Outros Correlatos' },
                { value: 'outros-correlatos-instituicao', label: 'Outros Correlatos - Instituição' },
                { value: 'softwares', label: 'Softwares' },
                { value: 'treinamentos', label: 'Treinamentos' },
                { value: 'livros-e-periodicos', label: 'Livros e Periódicos' },
                { value: 'instituicao-projeto-externo', label: 'Instituição - Projeto Externo' },
                { value: 'viagens', label: 'Viagens' },
            ];
            setTypologies(otherExpenditureTypes);

            setProjectsNames([{ value: 'proj1', label: 'Projeto Alpha' }, { value: 'proj2', label: 'Projeto Beta' }]);
            setMacroStages([{ value: 'etapa1', label: 'Etapa 1' }, { value: 'etapa2', label: 'Etapa 2' }]);
            setAppropriationTypes([{ value: 'aprop1', label: 'Apropriação Tipo A' }]);
        } finally {
            toggleLoading(false);
        }
    }

    useEffect(() => {
       
        getSelectOptions();
    }, []);

    useEffect(() => {
        async function loadExpenditureData() {
            if (isEditMode && projectsNames.length > 0) { 
                toggleLoading(true);
                try {
                    const response = await api.get(`OtherExpenditure/GetById/${id}`);
                    if (response.data.statusCode !== 200) {
                        toast.error("Falha ao carregar dados para edição.");
                        return;
                    }

                    const expenditure = response.data.data;
                    
                    setValue("Typology", expenditure.typology);
                    setValue("Associated", cnpjMask(expenditure.associatedCNPJ));
                    setValue("Supplier", expenditure.supplier);
                    setValue("CNPJ", cnpjMask(expenditure.supplierCNPJ));
                    setValue("ServiceProductDescription", expenditure.serviceProductDescription);
                    setValue("Quantity", expenditure.quantity);
                    setValue("Justification", expenditure.justification);
                    setValue("ProjectName", expenditure.projectName);
                    setValue("ProjectMacroStage", expenditure.projectMacroStage);
                    setValue("AppropriationType", expenditure.appropriationType);
                    setValue("AccountingAccount", expenditure.accountingAccount);
                    setValue("ManagementControl", expenditure.managementControl);
                    setValue("TrackingNumber", expenditure.trackingNumber);
                    setValue("AccountingDate", expenditure.accountingDate);
                    setValue("ExpenditureValue", expenditure.expenditureValue);
                    setValue("Observations", expenditure.observations);

                    setTypologySelected(expenditure.typology);
                    setProjectIdSelected(expenditure.projectId);
                    setAppropriationTypeSelected(expenditure.appropriationType);
                    setMacroStageSelected(expenditure.projectMacroStage);

                } catch (error) {
                    toast.error("Não foi possível carregar os dados do dispêndio para edição.");
                } finally {
                    toggleLoading(false);
                }
            }
        }
        loadExpenditureData();
    }, [isEditMode, id, projectsNames.length]); 

    useEffect(() => {
        async function fetchMacroStages() {
            if (projectIdSelected) {
                toggleLoading(true);
                try {
                    const response = await api.get(`OtherExpenditure/GetMacroStagesByProject/${company.serviceId}/${projectIdSelected}`);
                    if (response.data.statusCode === 200) {
                        setMacroStages(response.data.data || []);
                    }
                } catch (error) {
                    toast.error("Falha ao buscar as Macro Etapas do projeto.");
                    setMacroStages([]);
                } finally {
                    toggleLoading(false);
                }
            } else {
                setMacroStages([]); 
            }
        }
        fetchMacroStages();
    }, [projectIdSelected]); 

    useEffect(() => {
         if (projectIdSelected) {
            const selectedProject = projectsNames.find(p => p.value === projectIdSelected);
            if (selectedProject) {
                setValue("ProjectName", selectedProject.label);
            }
        } else {
            setValue("ProjectName", "");
        }
    }, [projectIdSelected, projectsNames]);

    function goToExpenditure() {
        navigate(`/${company.serviceId}/expenditure`);
    }

    return (
        <>
            <div className='card'>
                <div className='breadCrumb header-card-path'>
                    <Link to={`/${company.serviceId}/home`} className='d-flex align-center header-path'>
                        <HomeOutlined className='breadCrumb-item-actual header-path-icon-home' />
                    </Link>
                    <span className='breadCrumb-separator header-path'>
                        <Link to={`/${company.serviceId}/home`} className='header-path'>Homepage</Link>
                    </span>
                    <span className='header-path breadCrumb-separator'>{' / '}</span>
                    <span className='header-path breadCrumb-separator'>
                        <Link to={`/${company.serviceId}/expenditure`} className='header-path'>Dispêndios</Link>
                    </span>
                    <span className='header-path breadCrumb-separator'>{' / '}</span>
                    <span className='header-path-actual breadCrumb-separator'>{isEditMode ? 'Edição' : 'Criação'} de Dispêndio - Outros</span>
                </div>

                <Form onFinish={handleSubmit(handleSaveExpenditure)} layout="vertical">
                    <div className="mx-4">
                        <p className="main-title">{isEditMode ? 'Edição' : 'Cadastro'} - Outros Dispêndios</p>
                    </div>

                    <div className="d-flex justify-content-between mx-4 mb-2">
                        <Form.Item label="Tipologia" className="w-25 pe-4" required>
                            <Select placeholder='Selecione uma opção' value={typologySelected} onChange={(v) => { setTypologySelected(v); setValue("Typology", v); }}>
                                {typologies.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Associado (CNPJ)" className="w-25 pe-4" required>
                            <Controller
                                name="Associated"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="00.000.000/0000-00"
                                        maxLength={18}
                                        onChange={(e) => field.onChange(cnpjMask(e.target.value))}
                                    />
                                )}
                            />
                        </Form.Item>
                        <Form.Item label="Fornecedor" className="w-25 pe-4" required>
                            <Controller
                                name="Supplier"
                                control={control}
                                render={({ field }) => <Input {...field} placeholder="Nome do fornecedor" />}
                            />
                        </Form.Item>
                        <Form.Item label="CNPJ" className="w-25" required>
                            <Controller
                                name="CNPJ"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="00.000.000/0000-00"
                                        maxLength={18}
                                        onChange={(e) => field.onChange(cnpjMask(e.target.value))}
                                    />
                                )}
                            />
                        </Form.Item>
                    </div>

                    <div className="mx-4 mb-2">
                        <Form.Item label="Descrição dos Serviços/Produtos" required>
                            <Controller
                                name="ServiceProductDescription"
                                control={control}
                                render={({ field }) => <TextArea {...field} rows={3} placeholder="Digite aqui..." />}
                            />
                        </Form.Item>
                    </div>

                    <div className="d-flex justify-content-between mx-4 mb-2">
                        <Form.Item label="Quantidade" className="w-25 pe-4">
                            <Controller
                                name="Quantity"
                                control={control}
                                render={({ field }) => (
                                    <InputNumber 
                                        {...field} 
                                        stringMode 
                                        style={{ width: '100%' }} 
                                        placeholder="0" 
                                        pattern="[0-9]*" 
                                    />
                                )}
                            />
                        </Form.Item>
                        <div className="w-75">
                            <Form.Item label="Justificativa" required>
                                <Controller
                                    name="Justification"
                                    control={control}
                                    render={({ field }) => <TextArea {...field} rows={1} placeholder="Digite a justificativa" />}
                                />
                            </Form.Item>
                        </div>
                    </div>

                    <div className="d-flex justify-content-between mx-4 mb-2">
                        <Form.Item label="Nome do Projeto" className="w-50 pe-4" required>
                            <Select placeholder='Selecione um projeto' value={projectIdSelected} onChange={(id) => setProjectIdSelected(id)}>
                                {projectsNames.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Macro Etapa do Projeto" className="w-50" required>
                            <Select placeholder='Selecione uma etapa' value={macroStageSelected} onChange={(v) => { setMacroStageSelected(v); setValue("ProjectMacroStage", v); }} disabled={macroStages.length === 0}>
                                {macroStages.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)}
                            </Select>
                        </Form.Item>
                    </div>

                    <div className="d-flex justify-content-between mx-4 mb-2">
                        <Form.Item label="Tipo de Apropriação" className="w-25 pe-4" required>
                            <Select placeholder='Selecione um tipo' value={appropriationTypeSelected} onChange={(v) => { setAppropriationTypeSelected(v); setValue("AppropriationType", v); }}>
                                {appropriationTypes.map(item => <Option key={item.value} value={item.value}>{item.label}</Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item label="Conta Contábil" className="w-25 pe-4" required>
                            <Controller
                                name="AccountingAccount"
                                control={control}
                                render={({ field }) => <Input {...field} placeholder="Número da conta" />}
                            />
                        </Form.Item>
                        <Form.Item label="Controle Gerencial" className="w-25 pe-4" required>
                            <Controller
                                name="ManagementControl"
                                control={control}
                                render={({ field }) => <Input {...field} placeholder="Controle gerencial" />}
                            />
                        </Form.Item>
                        <Form.Item label="N° Rastreio" className="w-25" required>
                            <Controller
                                name="TrackingNumber"
                                control={control}
                                render={({ field }) => <Input {...field} placeholder="Número de rastreio" />}
                            />
                        </Form.Item>
                    </div>

                    <div className="d-flex justify-content-between mx-4 mb-2">
                        <Form.Item label="Data de Contabilização" className="w-50 pe-4" required>
                            <Controller
                                name="AccountingDate"
                                control={control}
                                render={({ field }) => (
                                    <DatePicker
                                        {...field}
                                        format={"DD/MM/YYYY"}
                                        placeholder="Clique para selecionar uma data"
                                        style={{ width: "100%" }}
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(date) => field.onChange(date ? date.toISOString() : null)}
                                    />
                                )}
                            />
                        </Form.Item>
                        <Form.Item label="Valor Dispêndio (R$)" className="w-50" required>
                            <Controller
                                name="ExpenditureValue"
                                control={control}
                               render={({ field }) => (
                                    <InputNumber
                                        {...field}
                                        style={{ width: '100%' }}
                                        placeholder="R$ 0,00"
                                        formatter={(value) => {
                                            if (!value) return '';
                                            const parts = String(value).split('.');
                                            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                                            return `R$ ${parts.join(',')}`;
                                        }}
                                        parser={(value) => {
                                            if (!value) return 0;
                                            const numericString = value.replace(/R\$\s?/g, '').replace(/\./g, '').replace(',', '.');
                                            return Number.isNaN(parseFloat(numericString)) ? 0 : parseFloat(numericString);
                                        }}
                                        precision={2}
                                        step={0.01}
                                    />
                                )}
                            />
                        </Form.Item>
                    </div>

                    <div className="mx-4 mb-2">
                        <Form.Item label="Observações">
                            <Controller
                                name="Observations"
                                control={control}
                                render={({ field }) => <TextArea {...field} rows={3} placeholder="Observações adicionais (opcional)" />}
                            />
                        </Form.Item>
                    </div>

                    <div className="d-flex justify-content-end align-items-center m-4">
                        <Button style={{ height: "40px", marginRight: "10px" }} onClick={goToExpenditure}>
                            <div>
                                Voltar
                            </div>
                        </Button>
                         <Button className='material-button-blue' type='primary' style={{ height: "40px" }} htmlType='submit' >
                            <div className='text-white'>
                                Salvar
                            </div>
                        </Button>
                    </div>
                </Form>
            </div>
        </>
    );
}